/**
 * Rich Text Editor with Quill
 * Features: Full toolbar, inline comments on selected text
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MessageSquarePlus,
  Send,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/* ── Custom Inline Comment Blot ── */
const Inline = Quill.import('blots/inline');

class InlineCommentBlot extends Inline {
  static blotName = 'inlineComment';
  static tagName = 'span';
  static className = 'inline-comment-highlight';

  static create(value) {
    const node = super.create();
    node.setAttribute('data-inline-comment', value);
    return node;
  }

  static formats(node) {
    return node.getAttribute('data-inline-comment');
  }

  format(name, value) {
    if (name === 'inlineComment' && value) {
      this.domNode.setAttribute('data-inline-comment', value);
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(InlineCommentBlot);

/* ── Quill Toolbar Configuration ── */
const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  ['clean'],
];

/* ── Main Rich Text Editor ── */
export function RichTextEditor({ content, onUpdate, onAddInlineComment, inlineComments = [], authorName = 'User', readOnly = false }) {
  const [commentInput, setCommentInput] = useState(null);
  const [hoveredComment, setHoveredComment] = useState(null);
  const editorWrapperRef = useRef(null);
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: readOnly ? false : TOOLBAR_OPTIONS,
    clipboard: { matchVisual: false },
  }), [readOnly]);

  const formats = useMemo(() => [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'align',
    'list', 'blockquote', 'code-block',
    'link', 'image', 'inlineComment',
  ], []);

  // Handle editor changes
  const handleChange = useCallback((value, delta, source) => {
    if (source === 'user' && onUpdate) {
      onUpdate(value);
    }
  }, [onUpdate]);

  // Add inline comment
  const handleAddComment = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection();
    if (!range || range.length === 0) return;

    const selectedText = editor.getText(range.index, range.length);
    setCommentInput({ index: range.index, length: range.length, selectedText, text: '' });
  }, []);

  const submitComment = useCallback(() => {
    if (!commentInput || !commentInput.text.trim()) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const commentId = `ic-${Date.now()}`;

    // Apply the inline comment format to the selected range
    editor.formatText(commentInput.index, commentInput.length, 'inlineComment', commentId, 'api');

    if (onAddInlineComment) {
      onAddInlineComment({
        id: commentId,
        text: commentInput.text.trim(),
        selectedText: commentInput.selectedText,
        authorName,
        from: commentInput.index,
        to: commentInput.index + commentInput.length,
      });
    }
    setCommentInput(null);
  }, [commentInput, onAddInlineComment, authorName]);

  const cancelComment = useCallback(() => setCommentInput(null), []);

  // Hover detection on inline comment highlights
  useEffect(() => {
    const wrapper = editorWrapperRef.current;
    if (!wrapper) return;

    const handleMouseOver = (e) => {
      const el = e.target.closest('.inline-comment-highlight');
      if (el) {
        const commentId = el.getAttribute('data-inline-comment');
        const comment = inlineComments.find(c => c.id === commentId);
        if (comment) {
          const rect = el.getBoundingClientRect();
          setHoveredComment({
            id: commentId,
            text: comment.text,
            author: comment.authorName || authorName,
            authorRole: comment.authorRole || '',
            selectedText: comment.selectedText,
            top: rect.bottom + 4,
            left: rect.left + rect.width / 2,
          });
        }
      }
    };

    const handleMouseOut = (e) => {
      const el = e.target.closest('.inline-comment-highlight');
      const related = e.relatedTarget?.closest?.('.inline-comment-highlight');
      if (el && !related) {
        setHoveredComment(null);
      }
    };

    wrapper.addEventListener('mouseover', handleMouseOver);
    wrapper.addEventListener('mouseout', handleMouseOut);
    return () => {
      wrapper.removeEventListener('mouseover', handleMouseOver);
      wrapper.removeEventListener('mouseout', handleMouseOut);
    };
  }, [inlineComments, authorName]);

  // Update readOnly state
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.enable(!readOnly);
    }
  }, [readOnly]);

  return (
    <div className="flex flex-col h-full rich-text-editor relative" ref={editorWrapperRef} data-testid="rich-text-editor">
      {/* Comment action button when not readOnly */}
      {!readOnly && (
        <div className="flex items-center gap-1 px-2 py-1 border-b bg-muted/30" data-testid="comment-action-bar">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1 text-amber-600 hover:text-amber-700"
            onClick={handleAddComment}
            data-testid="add-inline-comment-btn"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
            Comment on Selection
          </Button>
        </div>
      )}

      {/* Comment Input Overlay */}
      {commentInput && (
        <div className="absolute right-4 top-16 z-20 w-72 bg-card border rounded-lg shadow-xl p-3 space-y-2" data-testid="comment-input-overlay">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Add Comment</p>
            <button onClick={cancelComment} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground italic truncate">
            "{commentInput.selectedText}"
          </p>
          <div className="flex gap-1.5">
            <Input
              autoFocus
              value={commentInput.text}
              onChange={e => setCommentInput(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Type your comment..."
              className="h-8 text-xs"
              onKeyDown={e => e.key === 'Enter' && submitComment()}
              data-testid="inline-comment-input"
            />
            <Button size="sm" className="h-8 px-2" onClick={submitComment} disabled={!commentInput.text.trim()} data-testid="inline-comment-submit">
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Hover Comment Tooltip */}
      {hoveredComment && (
        <div
          className="fixed z-50 max-w-xs bg-popover border shadow-lg rounded-lg p-3 pointer-events-none"
          style={{ top: hoveredComment.top, left: hoveredComment.left, transform: 'translateX(-50%)' }}
          data-testid="comment-hover-tooltip"
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center text-[9px] font-bold text-white shrink-0">
              {hoveredComment.author?.charAt(0)}
            </div>
            <span className="text-xs font-semibold text-foreground">{hoveredComment.author}</span>
            {hoveredComment.authorRole && (
              <span className="text-[10px] text-muted-foreground ml-auto">{hoveredComment.authorRole}</span>
            )}
          </div>
          <p className="text-xs text-foreground/80 leading-relaxed">{hoveredComment.text}</p>
        </div>
      )}

      {/* Quill Editor */}
      <div className="flex-1 overflow-auto quill-editor-wrapper">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={content || ''}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          readOnly={readOnly}
          placeholder="Start writing..."
        />
      </div>

      {/* Editor Styles */}
      <style>{`
        /* Fix Tailwind preflight breaking Quill toolbar SVG icons */
        .quill-editor-wrapper .ql-toolbar svg {
          display: inline-block !important;
          width: 18px;
          height: 18px;
        }
        .quill-editor-wrapper .ql-toolbar button {
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          float: none !important;
        }
        .quill-editor-wrapper .ql-toolbar .ql-formats {
          display: inline-flex !important;
          align-items: center;
          gap: 2px;
          vertical-align: middle;
        }
        .quill-editor-wrapper .quill {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .quill-editor-wrapper .ql-toolbar.ql-snow {
          border: none !important;
          border-bottom: 1px solid hsl(var(--border, 214.3 31.8% 91.4%)) !important;
          background: hsl(var(--muted, 210 40% 96.1%) / 0.3);
          padding: 8px 12px;
          position: sticky;
          top: 0;
          z-index: 10;
          flex-shrink: 0;
        }
        .quill-editor-wrapper .ql-container.ql-snow {
          border: none !important;
          flex: 1;
          font-size: 0.9375rem;
          line-height: 1.7;
          overflow-y: auto;
        }
        .quill-editor-wrapper .ql-editor {
          padding: 2rem;
          min-height: 100%;
          color: hsl(var(--foreground, 222.2 84% 4.9%));
        }
        .quill-editor-wrapper .ql-editor.ql-blank::before {
          color: hsl(var(--muted-foreground, 215.4 16.3% 46.9%));
          font-style: normal;
        }
        .quill-editor-wrapper .ql-editor h1 {
          font-size: 1.75rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          line-height: 1.3;
          color: hsl(221, 83%, 53%);
        }
        .quill-editor-wrapper .ql-editor h2 {
          font-size: 1.35rem;
          font-weight: 600;
          margin-top: 1.75rem;
          margin-bottom: 0.5rem;
          line-height: 1.35;
          color: hsl(221, 83%, 53%);
        }
        .quill-editor-wrapper .ql-editor h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }
        .quill-editor-wrapper .ql-editor p {
          margin-bottom: 0.75rem;
        }
        .quill-editor-wrapper .ql-editor ul,
        .quill-editor-wrapper .ql-editor ol {
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .quill-editor-wrapper .ql-editor li {
          margin-bottom: 0.25rem;
        }
        .quill-editor-wrapper .ql-editor blockquote {
          border-left: 3px solid hsl(221, 83%, 53%);
          padding-left: 1rem;
          margin: 1rem 0;
          color: hsl(var(--muted-foreground, 215.4 16.3% 46.9%));
          font-style: italic;
        }
        .quill-editor-wrapper .ql-editor hr {
          border: none;
          border-top: 1px solid hsl(var(--border, 214.3 31.8% 91.4%));
          margin: 1.5rem 0;
        }
        .quill-editor-wrapper .ql-editor code {
          background: hsl(var(--muted, 210 40% 96.1%));
          border-radius: 0.25rem;
          padding: 0.15rem 0.4rem;
          font-size: 0.875em;
        }
        .quill-editor-wrapper .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          border: 1px solid hsl(var(--border, 214.3 31.8% 91.4%));
          margin: 1rem 0;
        }
        .inline-comment-highlight {
          background-color: rgba(255, 183, 77, 0.35);
          border-bottom: 2px solid #ff9800;
          cursor: pointer;
          padding: 0 1px;
        }
        /* Toolbar icon colors - use #444 default, Quill's own default */
        .quill-editor-wrapper .ql-snow .ql-stroke {
          stroke: #444 !important;
        }
        .quill-editor-wrapper .ql-snow .ql-fill,
        .quill-editor-wrapper .ql-snow .ql-stroke.ql-fill {
          fill: #444 !important;
        }
        .quill-editor-wrapper .ql-snow button:hover .ql-stroke,
        .quill-editor-wrapper .ql-snow button.ql-active .ql-stroke,
        .quill-editor-wrapper .ql-snow .ql-picker-label:hover .ql-stroke,
        .quill-editor-wrapper .ql-snow .ql-picker-label.ql-active .ql-stroke {
          stroke: #06c !important;
        }
        .quill-editor-wrapper .ql-snow button:hover .ql-fill,
        .quill-editor-wrapper .ql-snow button.ql-active .ql-fill,
        .quill-editor-wrapper .ql-snow .ql-picker-label:hover .ql-fill,
        .quill-editor-wrapper .ql-snow .ql-picker-label.ql-active .ql-fill {
          fill: #06c !important;
        }
        /* Picker dropdowns */
        .quill-editor-wrapper .ql-snow .ql-picker-label {
          color: hsl(var(--foreground, 222.2 84% 4.9%));
        }
        .quill-editor-wrapper .ql-snow .ql-picker-options {
          background-color: hsl(var(--card, 0 0% 100%));
          border-color: hsl(var(--border, 214.3 31.8% 91.4%));
        }
        /* Tooltip styling */
        .quill-editor-wrapper .ql-snow .ql-tooltip {
          background-color: hsl(var(--card, 0 0% 100%));
          border-color: hsl(var(--border, 214.3 31.8% 91.4%));
          color: hsl(var(--foreground, 222.2 84% 4.9%));
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          z-index: 20;
        }
      `}</style>
    </div>
  );
}

export default RichTextEditor;
